// src/services/api.js
import axios from "axios";

// Create a single axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

// Inject latest access token on every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    } else if (config.headers && config.headers.Authorization) {
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Refresh token on 401 and retry once
let isRefreshing = false;
let pendingRequestsQueue = [];

const processQueue = (error, token = null) => {
  pendingRequestsQueue.forEach(({ resolve, reject, requestConfig }) => {
    if (token) {
      requestConfig.headers = requestConfig.headers || {};
      requestConfig.headers.Authorization = `Bearer ${token}`;
      resolve(api(requestConfig));
    } else {
      reject(error);
    }
  });
  pendingRequestsQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;

    if (status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue the request until the refresh finishes
        return new Promise((resolve, reject) => {
          pendingRequestsQueue.push({
            resolve,
            reject,
            requestConfig: originalRequest,
          });
        });
      }

      isRefreshing = true;
      try {
        const refreshUrl = `${process.env.REACT_APP_API_URL}token/refresh/`;
        const refreshResponse = await axios.post(
          refreshUrl,
          { refresh: refreshToken },
          { withCredentials: true }
        );
        const { access, refresh } = refreshResponse.data || {};

        if (access) localStorage.setItem("token", access);
        if (refresh) localStorage.setItem("refreshToken", refresh);

        // Update header and retry original
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${access}`;

        processQueue(null, access);
        return api(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Switch Request APIs
export const switchRequestAPI = {
  // Library Switch Requests
  library: {
    // Get available seats for dropdowns
    getAvailableSeats: () => api.get("/library/seats/?is_booked=false"),

    // Get live map data for switch options
    getLiveMapData: () => api.get("/library/seats/live-map/"),

    // Available Switch Requests
    createAvailableSwitchRequest: (data) =>
      api.post("/library/switch-requests/available/", data),

    getAvailableSwitchRequests: (params = {}) =>
      api.get("/library/switch-requests/available/", { params }),

    getAvailableSwitchRequest: (id) =>
      api.get(`/library/switch-requests/available/${id}/`),

    approveAvailableSwitchRequest: (id) =>
      api.post(`/library/switch-requests/available/${id}/approve/`),

    rejectAvailableSwitchRequest: (id, data) =>
      api.post(`/library/switch-requests/available/${id}/reject/`, data),

    cancelAvailableSwitchRequest: (id, data) =>
      api.post(`/library/switch-requests/available/${id}/cancel/`, data),

    // Mutual Switch Requests
    createMutualSwitchRequest: (data) =>
      api.post("/library/switch-requests/mutual/", data),

    getMutualSwitchRequests: (params = {}) =>
      api.get("/library/switch-requests/mutual/", { params }),

    getMutualSwitchRequest: (id) =>
      api.get(`/library/switch-requests/mutual/${id}/`),

    matchAndApproveMutualSwitch: (data) =>
      api.post("/library/switch-requests/mutual/match-and-approve/", data),

    rejectMutualSwitchRequest: (id, data) =>
      api.post(`/library/switch-requests/mutual/${id}/reject/`, data),

    cancelMutualSwitchRequest: (id, data) =>
      api.post(`/library/switch-requests/mutual/${id}/cancel/`, data),

    // Legacy endpoints (keeping for backward compatibility)
    createSwitchRequest: (data) => api.post("/library/switch-requests/", data),
    getSwitchRequests: () => api.get("/library/switch-requests/"),

    // Admin approve available seat switch
    approveSwitch: (id) => api.post(`/library/switch-requests/${id}/approve/`),

    // Admin reject switch request
    rejectSwitch: (id, data) =>
      api.post(`/library/switch-requests/${id}/reject/`, data),

    // Admin manually match and approve mutual requests
    matchAndApprove: (data) =>
      api.post("/library/switch-requests/match-and-approve/", data),

    // Get booking switch history
    getSwitchHistory: () => api.get("/library/booking-switch-history/"),

    // New history endpoints
    getAvailableSwitchHistory: () => api.get("/library/switch-history/available/"),
    getMutualSwitchHistory: () => api.get("/library/switch-history/mutual/"),
  },
};

export default api;